/* Helpers */
import {
  parseSidebarCustomLinks,
  request,
  templateBuilder,
} from '../helpers/mainHelpers.js';

export default {
  state: {
    quickLinks: {},
    additionalContainer: '',
    compatibleProducts: {},
    compatibleProductsPrices: {},
    showSidebar: false,
    compatibleProductsLoaded: false,
  },
  getters: {
    getShowSidebar(state) {
      return state.showSidebar;
    },
    getSidebarQuickLinks(state) {
      return state.quickLinks;
    },
    getSidebarAdditional(state) {
      return state.additionalContainer;
    },
    getSidebarAdditionalContent(state, getters) {
      const additionalContainer = getters.getSidebarAdditional;
      return additionalContainer ? additionalContainer.content : '';
    },
    getShowSidebarAdditionalContent(state, getters) {
      const content = getters.getSidebarAdditionalContent;
      const showSidebar = getters.getShowSidebar;
      return content && showSidebar;
    },
    getSoftwareLink(state, getters) {
      const quicklinks = getters.getSidebarQuickLinks;
      return quicklinks.softwareLink;
    },
    getShowSoftwareLink(state, getters) {
      const quicklinks = getters.getSidebarQuickLinks;
      return quicklinks.showSoftware;
    },
    getCompatibleProducts(state) {
      return state.compatibleProducts;
    },
    getCompatibleProductsByProductId(state, getters) {
      return (productId) => getters.getCompatibleProducts[productId];
    },
    getActiveCompatibleProducts(state, getters) {
      const productId = getters.getProductId;
      const products = getters.getCompatibleProductsByProductId(productId);
      return products || [];
    },
    getHasActiveCompatibleProducts(state, getters) {
      return !!getters.getActiveCompatibleProducts.length;
    },
    getCompatibleProductPrices(state) {
      return state.compatibleProductsPrices;
    },
    getCompatibleProductPriceByUrl(state, getters) {
      return (priceUrl) => getters.getCompatibleProductPrices[priceUrl];
    },
    getManualsLink(state, getters) {
      const partNumber = getters.getPartNumber;
      const urlTemplate = getters.getManualsUrl;
      const locale = getters.getLocale;
      const localeParts = locale.split('-');

      if (!urlTemplate) {
        return null;
      }

      return templateBuilder(urlTemplate, {
        language: localeParts[0],
        country: localeParts[1],
        partNum: partNumber,
      });
    },
    getCompatibleProductsLoaded(state) {
      return state.compatibleProductsLoaded;
    },
  },
  mutations: {
    setCompatibleProduct(state, payload) {
      const { data, productId } = payload;
      const products = state.compatibleProducts[productId];

      state.compatibleProducts[productId] = products.map((item) => {
        if (item.productId === data.productId) {
          return data;
        }
        return item;
      });
    },
    setCompatibleProducts(state, payload) {
      const { data, productId } = payload;
      state.compatibleProducts = { ...state.compatibleProducts, [productId]: data };
    },
    setCompatibleProductPrice(state, payload) {
      const { data, priceUrl } = payload;
      state.compatibleProductsPrices = {
        ...state.compatibleProductsPrices,
        [priceUrl]: data,
      };
    },
    setQuickLinks(state, payload) {
      state.quickLinks = payload;
    },
    setAdditionalContainer(state, payload) {
      state.additionalContainer = payload;
    },
    setShowSidebar(state, payload) {
      state.showSidebar = payload;
    },
    setCompatibleProductsLoaded(state, payload) {
      state.compatibleProductsLoaded = payload;
    },
  },
  actions: {
    setSidebar({ commit }, payload) {
      if (payload) {
        const { content } = payload.quickLinks;
        payload.quickLinks.content = parseSidebarCustomLinks(content);
        commit('setQuickLinks', payload.quickLinks);
        commit('setAdditionalContainer', payload.additionalContainer);
        commit('setShowSidebar', true);
      } else {
        // Reset quickLinks and AdditionalContainer to default values if no payload data
        commit('setQuickLinks', {});
        commit('setAdditionalContainer', '');
      }
    },
    async callCompatibleProducts({ commit, getters, rootGetters }) {
      const { getCustomerGroups } = rootGetters;
      const {
        getProductId,
        getCompatibleProducts,
        getLocale,
        getBuyGarminEndpoint,
        getCompatibleProductsJsonUrl,
      } = getters;
      const localeParts = getLocale.split('-');

      if (!getCompatibleProductsJsonUrl || getCompatibleProducts[getProductId]) {
        return;
      }

      const url = templateBuilder(getCompatibleProductsJsonUrl, {
        productId: getProductId,
        country: localeParts[1],
        locale: getLocale,
      });

      const data = await request({
        url,
      }).catch(() => {
        throw new Error('Unable to call compatibleProducts service');
      });

      const compatibleProducts = data?.associationProducts;

      if (!data || !Array.isArray(compatibleProducts || !compatibleProducts?.length)) {
        return;
      }

      const productsCache = {
        productId: getProductId,
        data: compatibleProducts,
      };

      // Commit the compatible products after fetching
      commit('setCompatibleProducts', productsCache);

      const queryParams = new URLSearchParams();
      queryParams.append('locale', getLocale);
      /**
       * If there are multiple customerGroups, they will be divided by a |
       * new URLSearchParams() will convert | to %7C which the pricing APIs expect when there are multiple customer groups
       */
      queryParams.append('customerGroup', getCustomerGroups);
      const locationHref = window?.location?.href;
      if (locationHref.includes('cdncache=false')) queryParams.append('cdncache', false);

      const compatibleProductsPricingRequests = compatibleProducts.map(async (product) => {
        // Make the API call for the pricing proxy
        const priceUrl = `${getBuyGarminEndpoint}/pricing-proxy-services/countries/${localeParts[1]}/skus/${product.partNumber}/price?${queryParams}`;

        // Check if product price has already been called and cached
        // returning null indicates the price was not called
        const cachedPrice = getters.getCompatibleProductPriceByUrl(priceUrl);

        // if the price call is already cached, return that, else call to get price
        const productPrice = cachedPrice || await request({
          url: priceUrl,
        }).catch(() => {
          console.error('Error calling pricing proxy service');
        });

        // Set price to empty object if no price returns
        if (!productPrice) {
          product.priceObj = {};
        }

        const newPrice = {
          data: productPrice,
          priceUrl,
        };

        // Add the price to the product
        product.priceObj = productPrice || {};

        // Commit the product again with the price updated
        commit('setCompatibleProduct', {
          productId: getProductId,
          data: product,
        });

        // Cache product price
        commit('setCompatibleProductPrice', newPrice);
      });

      try {
        await Promise.all(compatibleProductsPricingRequests);
        commit('setCompatibleProductsLoaded', true);
      } catch (e) {
        console.error(`Unable to fetch Compatible Products pricing requests: ${e}`);
      }
    },
  },
};
